import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API, fmtMoney, categoryLabel } from "@/lib/api";
import { Lock } from "@phosphor-icons/react";

export default function HeirsView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [assets, setAssets] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    api.get(`/share/${token}`, { params: { page: 1, per_page: 50 } })
      .then((r) => {
        setData(r.data);
        setAssets(r.data.assets || []);
        setPagination(r.data.pagination);
      })
      .catch((e) => setErr(e?.response?.status === 404 ? "not_found" : "error"))
      .finally(() => setLoading(false));
  }, [token]);

  const loadMore = async () => {
    if (!pagination?.has_more || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = pagination.page + 1;
      const r = await api.get(`/share/${token}`, { params: { page: next, per_page: pagination.per_page } });
      setAssets((prev) => [...prev, ...(r.data.assets || [])]);
      setPagination(r.data.pagination);
    } catch (err) {
      // Surface pagination failures so heirs aren't left staring at a
      // spinner that never resolves (e.g. expired share link → 410).
      const status = err?.response?.status;
      if (status === 410) {
        toast.error("This share link has expired. Ask the owner for a fresh one.");
      } else {
        toast.error("Could not load more entries. Please try again.");
      }
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen ledger-paper flex items-center justify-center">
        <p className="font-mono-ledger text-xs text-[#5C5F58] uppercase tracking-[0.25em]">Opening the record…</p>
      </div>
    );
  }

  if (err === "not_found") {
    return (
      <div className="min-h-screen ledger-paper flex items-center justify-center p-6" data-testid="heirs-not-found">
        <div className="max-w-md text-center border border-[#C9C4B8] bg-white p-10">
          <Lock size={32} color="#6B1718" weight="regular" className="mx-auto mb-4" />
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#6B1718] mb-2">
            Access Revoked
          </div>
          <p className="font-serif-display text-3xl text-[#0A192F] mb-3">This link is no longer valid.</p>
          <p className="text-sm text-[#5C5F58]">
            The owner has either revoked this share link or it has expired. Please request a new link.
          </p>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen ledger-paper flex items-center justify-center p-6">
        <p className="text-sm text-[#6B1718]">Something went wrong. Please try again.</p>
      </div>
    );
  }

  const { label, generated_at, permissions, home_components = [] } = data || {};
  const grouped = {};
  for (const a of assets) {
    const key = a.category || "other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(a);
  }
  const order = ["fine_art", "high_end_gear", "vehicle_marine", "home_component", "other"];
  const orderedKeys = order.filter((k) => grouped[k]?.length).concat(
    Object.keys(grouped).filter((k) => !order.includes(k))
  );

  return (
    <div className="min-h-screen ledger-paper" data-testid="heirs-view">
      <header className="border-b border-[#C9C4B8] bg-[#F9F8F6]/95 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo-mark.png"
              alt="The Asset Arbor"
              className="w-10 h-10 object-contain shrink-0"
              draggable="false"
            />
            <div>
              <div className="font-serif-display text-2xl text-[#0A192F] leading-none">The Asset Arbor</div>
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">Heirs · Read-Only View</div>
            </div>
          </div>
          <div className="hidden md:block text-right">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">Generated</div>
            <div className="font-mono-ledger text-xs text-[#0A192F]">
              {generated_at ? new Date(generated_at).toLocaleDateString() : "—"}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-8 py-10 md:py-16 space-y-12">
        <section className="fade-in-up">
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-3">
            {label || "For Heirs & Executors"}
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl text-[#0A192F] leading-[0.95] mb-4">
            The Estate<br />Manifest.
          </h1>
          <p className="text-sm text-[#5C5F58] max-w-2xl leading-relaxed">
            This is the owner&apos;s recorded inventory of physical assets and home components, prepared as a reference
            for family members, executors, and counsel. {permissions?.dispensation ? "Dispensation directives reflect the owner's recorded intent." : ""}
            {" "}These records are not legally binding unless formally incorporated into a will or trust.
          </p>
        </section>

        {assets.length === 0 ? (
          <p className="text-sm text-[#5C5F58] italic border border-[#E0DDD6] bg-white p-6">
            No assets are currently recorded on the ledger.
          </p>
        ) : (
          orderedKeys.map((cat) => (
            <section key={cat} data-testid={`heirs-section-${cat}`} className="space-y-3">
              <div className="flex items-end justify-between border-b border-[#C9C4B8] pb-2">
                <h2 className="font-serif-display text-3xl text-[#0A192F]">{categoryLabel(cat)}</h2>
                <span className="font-mono-ledger text-xs text-[#5C5F58] tabular-nums">
                  {grouped[cat].length} item(s)
                </span>
              </div>
              <ul className="divide-y divide-[#E0DDD6] bg-white border border-[#E0DDD6]">
                {grouped[cat].map((a) => (
                  <li key={a.id} className="p-6" data-testid={`heirs-asset-${a.id}`}>
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-serif-display text-2xl text-[#0A192F] leading-tight">{a.name}</h3>
                        {(a.brand || a.model) && (
                          <p className="font-mono-ledger text-sm text-[#5C5F58] mt-1">
                            {[a.brand, a.model].filter(Boolean).join(" · ")}
                          </p>
                        )}
                        <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                          {a.serial && <Field k="Serial" v={a.serial} mono />}
                          {(a.location || a.sub_location) && (
                            <Field
                              k="Room"
                              v={
                                a.location && a.sub_location
                                  ? `${a.location} · ${a.sub_location}`
                                  : a.location || a.sub_location
                              }
                            />
                          )}
                          {a.condition && <Field k="Condition" v={a.condition} />}
                          {a.provenance && <Field k="Provenance" v={a.provenance} />}
                          {permissions?.valuation && a.fmv ? <Field k="Fair Market" v={fmtMoney(a.fmv)} mono /> : null}
                          {permissions?.valuation && a.rcv ? <Field k="Replacement Cost" v={fmtMoney(a.rcv)} mono /> : null}
                        </dl>

                        {permissions?.dispensation && a.dispensation && (
                          <div className="mt-4 bg-[#F9F8F6] border-l-2 border-[#6B1718] p-4">
                            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#6B1718] mb-2">
                              Dispensation Directive
                            </div>
                            <p className="text-sm text-[#0A192F] whitespace-pre-wrap leading-relaxed">
                              {a.dispensation}
                            </p>
                          </div>
                        )}
                      </div>

                      {permissions?.photos && a.photo_paths?.length > 0 && (
                        <div className="md:w-48 shrink-0">
                          <div className="grid grid-cols-2 gap-1">
                            {a.photo_paths.slice(0, 4).map((p) => (
                              <a
                                key={p}
                                href={`${API}/share/${token}/files/${p}`}
                                target="_blank"
                                rel="noreferrer"
                                className="block aspect-square border border-[#E0DDD6] bg-[#F9F8F6] overflow-hidden"
                              >
                                <img src={`${API}/share/${token}/files/${p}`} alt="" className="w-full h-full object-cover" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}

        {home_components.length > 0 && (
          <section className="space-y-3" data-testid="heirs-home-section">
            <div className="flex items-end justify-between border-b border-[#C9C4B8] pb-2">
              <h2 className="font-serif-display text-3xl text-[#0A192F]">Home Components</h2>
              <span className="font-mono-ledger text-xs text-[#5C5F58] tabular-nums">
                {home_components.length} record(s)
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#E0DDD6] bg-white">
              {home_components.map((c) => (
                <div key={c.id} className="p-5 border-b border-r border-[#E0DDD6]">
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                    {(c.component_type || "").replace(/_/g, " ")}
                  </div>
                  <h3 className="font-serif-display text-xl text-[#0A192F] leading-tight">{c.name || c.label}</h3>
                  <dl className="mt-2 space-y-1 text-sm">
                    {(c.room || c.location) && <Field k="Room" v={c.room || c.location} />}
                    {c.material && <Field k="Material" v={c.material} />}
                    {c.paint_code && <Field k="Paint Code" v={c.paint_code} mono />}
                    {c.install_date && <Field k="Installed" v={c.install_date} />}
                    {c.warranty_expiry && <Field k="Warranty" v={c.warranty_expiry} />}
                    {c.specs && <Field k="Specs" v={c.specs} />}
                  </dl>
                </div>
              ))}
            </div>
          </section>
        )}

        {pagination?.has_more && (
          <div className="flex justify-center pt-4" data-testid="heirs-load-more-wrap">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="heirs-load-more"
              className="px-6 py-3 text-sm border border-[#0A192F] bg-white text-[#0A192F] hover:bg-[#0A192F] hover:text-[#F9F8F6] disabled:opacity-60"
            >
              {loadingMore
                ? "Loading…"
                : `Load more · ${pagination.total - assets.length} remaining`}
            </button>
          </div>
        )}

        <footer className="border-t border-[#C9C4B8] pt-6 text-center">
          <p className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
            — End of Manifest —
          </p>
          <Link to="/" className="inline-block mt-4 text-xs text-[#1A3622] underline underline-offset-4">
            About The Asset Arbor
          </Link>
        </footer>
      </main>
    </div>
  );
}

function Field({ k, v, mono }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58] pt-0.5 min-w-[96px]">
        {k}
      </dt>
      <dd className={`text-[#0A192F] text-sm ${mono ? "font-mono-ledger" : ""}`}>{v}</dd>
    </div>
  );
}
